import { useMemo, useState } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors, closestCorners
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import LeadCard from './LeadCard';
import { STAGES, STAGE_COLORS } from '../lib/stages';
import { moveLeadStage } from '../lib/leads';

export default function KanbanBoard({ leads, onOpenLead }) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  // Group leads by stage. We keep a local copy so the optimistic
  // update on drop is instant — Firestore catches up via onSnapshot.
  const grouped = useMemo(() => {
    const g = Object.fromEntries(STAGES.map(s => [s, []]));
    for (const l of leads) {
      const stage = STAGES.includes(l.stage) ? l.stage : 'Prospect';
      g[stage].push(l);
    }
    return g;
  }, [leads]);

  const activeLead = activeId ? leads.find(l => l.id === activeId) : null;

  async function onDragEnd(event) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeLead = leads.find(l => l.id === active.id);
    if (!activeLead) return;

    // `over.id` will be either a stage column id (e.g. "stage:Qualified")
    // or another card id. Resolve to a target stage either way.
    let targetStage;
    if (typeof over.id === 'string' && over.id.startsWith('stage:')) {
      targetStage = over.id.slice('stage:'.length);
    } else {
      const overLead = leads.find(l => l.id === over.id);
      targetStage = overLead?.stage;
    }
    if (!targetStage || targetStage === activeLead.stage) return;

    try {
      await moveLeadStage(activeLead.id, targetStage);
    } catch (e) {
      console.error('Failed to move lead', e);
      alert('Could not move lead — your changes will retry when you reconnect.');
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={e => setActiveId(e.active.id)}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={onDragEnd}
    >
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {STAGES.map(stage => (
            <Column key={stage} stage={stage} leads={grouped[stage]} onOpenLead={onOpenLead} />
          ))}
        </div>
      </div>
      <DragOverlay>
        {activeLead && <LeadCard lead={activeLead} />}
      </DragOverlay>
    </DndContext>
  );
}

function Column({ stage, leads, onOpenLead }) {
  const { setNodeRef, isOver } = useDroppable({ id: `stage:${stage}` });
  const totalKES = leads.reduce((s, l) => s + Number(l.estimatedMonthlyValueKES || 0), 0);
  return (
    <div
      ref={setNodeRef}
      className={`w-72 shrink-0 rounded-xl p-2 border ${isOver ? 'bg-brand-50 border-brand-300' : 'bg-slate-100 border-slate-200'}`}
    >
      <div className="flex items-center justify-between px-2 py-1.5">
        <div className="flex items-center gap-2">
          <span className={`chip border ${STAGE_COLORS[stage]}`}>{stage}</span>
          <span className="text-xs text-slate-500">{leads.length}</span>
        </div>
        <div className="text-xs text-slate-500">
          {totalKES ? `KES ${totalKES.toLocaleString()}` : ''}
        </div>
      </div>
      <SortableContext items={leads.map(l => l.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2 min-h-[60px]">
          {leads.map(l => <LeadCard key={l.id} lead={l} onOpen={onOpenLead} />)}
          {leads.length === 0 && (
            <div className="text-xs text-slate-400 text-center py-4">Drop leads here</div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}
